import React, { useState } from 'react';
import Image from 'next/image';
import { cx } from '@/utils';
import { Wrapper } from '@googlemaps/react-wrapper';
import { env } from 'env.mjs';
import { motion } from 'framer-motion';
import moment from 'moment';

import { ButtonWithLogo } from '@/components/common/ButtonWithLogo/ButtonWithLogo';
import GeolocationButton from '@/components/common/GoogleMaps/GeolocationButton';
import ArrowLeftPagination from '@/components/common/Icons/ArrowLeftPagination';
import SvgCalendarDay from '@/components/common/Icons/CalendarDay';
import SvgClose from '@/components/common/Icons/Close';
import { SvgCommunity } from '@/components/common/Icons/Community';
import SvgEmail from '@/components/common/Icons/Email';
import { SvgGallery } from '@/components/common/Icons/Gallery';
import { SvgLogout } from '@/components/common/Icons/Logout';
import SvgMapMarker from '@/components/common/Icons/MapMarker';
import SvgPhone from '@/components/common/Icons/Phone';
import SvgTimer from '@/components/common/Icons/Timer';
import type { EventsItem } from '@/components/common/Providers/GraphqlProvider/generated';
import { getEventStatus } from '@/components/common/Utils/utils';

import { loadingRender, MapComponent, Marker } from '../../Admin/components/ButtonUbication/Maps';
import { EventInfo } from './EventItem';

type Props = {
  eventItem: EventsItem;
  closeMap?: () => void;
  center: google.maps.LatLngLiteral | undefined;
  zoom: number;
  clicks: google.maps.LatLng | undefined;
};

export const EventDetailInMap = ({ eventItem, closeMap, center, zoom, clicks }: Props) => {
  const [menuOpened, setMenuOpened] = useState(true);

  function onPressGeolocationButton() {
    document.getElementById('get_location_route_button')?.click();
  }

  return (
    <div className="flex h-[90dvh] w-full flex-col bg-white">
      <div className="flex h-[52px] items-center justify-between border-b border-b-silver px-[1rem]">
        <p className="font-montserrat text-[1.5rem] font-semibold text-primary">
          Ubicación del evento
        </p>
        <SvgClose onClick={closeMap} className="cursor-pointer text-tundora" />
      </div>
      <Wrapper apiKey={env.NEXT_PUBLIC_GOOGLE_MAP_KEY} render={loadingRender}>
        <MapComponent
          mapTypeControl={false}
          rotateControl
          center={center}
          zoom={zoom}
          style={{
            flexGrow: '1' /* esto si el elemento padre es flex y se desea que abarque todo */,
          }}
        >
          <Marker position={clicks} />
          <GeolocationButton
            map={undefined}
            destination={{ lat: center?.lat ?? 0, lng: center?.lng ?? 0 }}
            className="invisible h-0 w-0 opacity-0"
          />
        </MapComponent>
      </Wrapper>
      <div className="flex h-[52px] items-center justify-center border-t border-t-[#BABABA] px-[1rem]">
        <ButtonWithLogo
          className="bg-primary text-white"
          type="button"
          onClick={() => onPressGeolocationButton()}
          logo={<SvgMapMarker className="h-[16px] w-[16px] text-white" />}
          nameButton={'Mi ubicación'}
        />
      </div>

      <motion.div
        animate={{
          width: menuOpened ? 310 : 72,
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 25,
          },
        }}
        className="absolute left-[1%] top-[10%] h-[78%] rounded-xl border bg-white p-6 shadow-[0_4px_4px_0px_rgba(0,0,0,0.25)]"
      >
        <div
          className="absolute right-[-17px] top-[50%] z-50 flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-full bg-primary"
          onClick={() => setMenuOpened(!menuOpened)}
        >
          <ArrowLeftPagination
            className={cx('text-white transition-all', {
              'rotate-180': !menuOpened,
            })}
          />
        </div>

        {menuOpened ? (
          <div className="relative flex h-full animate-fade-in flex-col duration-75">
            <p className="font-montserrat text-[24px] font-semibold leading-[36px] text-primary">
              Detalles del evento
            </p>
            <hr className="border border-primary" />
            <Image
              alt="ImageEvent"
              src={eventItem.image?.downloadUrl ?? ''}
              width={290}
              height={152}
              className="w-257px mt-[1rem] h-[152px] rounded-[1rem]"
              quality={40}
            />
            <div
              id="customDivScroll"
              className="mt-[1rem] flex flex-auto flex-col gap-[20px] overflow-y-scroll"
            >
              <EventInfo
                icon={
                  <SvgCalendarDay color="primaryDark" className="h-[24px] w-[24px] fill-primary" />
                }
                title="Fecha de inicio"
                subtitle={moment.utc(eventItem.startDate).format('DD/MM/YYYY h:mm a')}
              />
              <EventInfo
                icon={
                  <SvgCalendarDay color="primaryDark" className="h-[24px] w-[24px] fill-primary" />
                }
                title="Fecha de finalización"
                subtitle={moment.utc(eventItem.endDate).format('DD/MM/YYYY h:mm a')}
              />
              <EventInfo
                icon={<SvgTimer className="h-[24px] w-[24px] text-primary" />}
                title="Estado"
                subtitle={getEventStatus(eventItem.eventStatus)}
              />
              {eventItem.address && (
                <EventInfo
                  icon={<SvgMapMarker className="h-[24px] w-[24px] text-primary" />}
                  title="Lugar del evento"
                  subtitle={eventItem.address}
                />
              )}
              {eventItem.phoneEvent && (
                <EventInfo
                  icon={<SvgPhone className="h-[24px] w-[24px] text-primary" />}
                  title="Teléfono"
                  subtitle={eventItem.phoneEvent}
                />
              )}
              {eventItem.emailEvent && (
                <EventInfo
                  icon={<SvgEmail className="h-[24px] w-[24px] text-primary" />}
                  title="Correo de contacto"
                  subtitle={eventItem.emailEvent}
                />
              )}
              {eventItem.groupEvent && (
                <EventInfo
                  icon={<SvgCommunity className="h-[24px] w-[24px] text-primary" />}
                  title="Dependencia o sectorial"
                  subtitle={eventItem.groupEvent}
                />
              )}
              {eventItem.paymentTypeEvent && (
                <EventInfo
                  icon={<SvgLogout className="h-[24px] w-[24px] text-primary" />}
                  title="Tipo de entrada"
                  subtitle={eventItem.paymentTypeEvent === 'Free' ? 'Gratis' : 'Con costo'}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex h-full animate-fade-in flex-col items-center justify-around gap-[25px] duration-75">
            <SvgGallery className="h-[24px] w-[24px] text-primary" />
            <SvgCalendarDay color="primaryDark" className="h-[24px] w-[24px] fill-primary" />
            <SvgCalendarDay color="primaryDark" className="h-[24px] w-[24px] fill-primary" />
            <SvgTimer className="h-[24px] w-[24px] text-primary" />
            {eventItem.address && <SvgMapMarker className="h-[24px] w-[24px] text-primary" />}
            {eventItem.phoneEvent && <SvgPhone className="h-[24px] w-[24px] text-primary" />}
            {eventItem.emailEvent && <SvgEmail className="h-[24px] w-[24px] text-primary" />}
            {eventItem.groupEvent && <SvgCommunity className="h-[24px] w-[24px] text-primary" />}
            {eventItem.paymentTypeEvent && <SvgLogout className="h-[24px] w-[24px] text-primary" />}
          </div>
        )}
      </motion.div>
    </div>
  );
};
